/**
 * Shared serialization types and constants with no runtime dependency on the serializer.
 */

import { SerializationVersion } from '../../const.js';

export const serializationVersionKey = '$version';

export type RefType = number;
export type Ref = { $ref: RefType };
export type ClassInfo = { $class: string };
export type PrefabInfo = { $path: string };
export type BaseInfo = ClassInfo | PrefabInfo;
export type Meta = { editorOnly?: boolean };

/** Leaf values that can appear in the serialized tree. */
export type SerializedPrimitive = string | number | boolean | null;

/**
 * An object node in the serialized tree. May contain arbitrary user-defined
 * keys as well as special `$`-prefixed meta-fields (`$base`, `$bc`, `$meta`,
 * `$length`, `$ref`). Unknown keys resolve to `unknown` so callers must narrow
 * before use.
 */
export interface SerializedObjectNode {
  $ref?: RefType;
  $base?: BaseInfo;
  $bc?: string;
  $meta?: Meta;
  $length?: number;
  [key: string]: unknown;
}

/** Array node in the serialized tree. */
export type SerializedArrayNode = SerializedValue[];

/** Any value that can appear inside the serialized tree. */
export type SerializedValue =
  | SerializedPrimitive
  | Ref
  | SerializedArrayNode
  | SerializedObjectNode;

export type SerializedSubObject = { $id?: RefType, $value: SerializedValue, $base?: BaseInfo, $meta?: Meta, $initOptions?: SerializedValue };
export type RootSerializedData = { $root: SerializedValue, $objects?: SerializedSubObject[] };
export type SerializedData = { [serializationVersionKey]: number } & RootSerializedData;

export const DumperFlags = {
  None: 0,
  AsPrefab: 1 << 0,
  Inline: 2 << 0,
} as const;
export type DumperFlags = typeof DumperFlags[keyof typeof DumperFlags];

export interface DumperOptions {
  flags?: DumperFlags,
}

export const LoaderFlags = {
  None: 0,
  WithoutEditor: 1 << 0,
} as const;
export type LoaderFlags = typeof LoaderFlags[keyof typeof LoaderFlags];

export interface LoaderOptions {
  flags?: LoaderFlags,
}

export const DumpValueResult = {
  Dumped: 0,
  SkippedTransient: 1,
  SkippedEqualToDefaultValue: 2,
  SkippedOtherwise: 3,
} as const;
export type DumpValueResult = typeof DumpValueResult[keyof typeof DumpValueResult];

export function isLegacyData(data: any): boolean {
  // no data, considered new, because we can load prefab without having to provide prefab data
  if (data === undefined) return false;

  const version = data?.[serializationVersionKey];
  if (version !== undefined) {
    // version clearly says it's legacy or new
    return version < SerializationVersion.V1;
  }

  // now we are in a state where we have a bunch of data, but without $version field
  return true;
}
